import * as React from 'react';
import { connect } from 'react-redux';
import Modal from '../../../BasicOrderInfoModal';
import { IEmailSender, ITemplate, IRecipient, IFile, IEmailFormState } from '../../../Types';
import { handleError } from '../../../../../../../shared/ErrorCatching';
import { Translate } from '../../../../../../../shared/LanguageService';
import EmailForm from './EmailForm';
import { assign } from '../../../../../../../shared/Assign';
import * as Actions from '../../../Actions';
import * as Strings from './../../Strings';
import notify from '../../../../../../../shared/feedback/notifications';
import { IAttachment } from '../../../Types';

interface IActions {
    createEmail: (
        orderId: number,
        formState: IEmailFormState,
        recipients: IRecipient[],
        attachments: number[]
    ) => any;
}

interface IProps {
    actions: IActions;
    close: () => void;
    recipients: IRecipient[];
    senders: IEmailSender[];
    templates: ITemplate[];
    defaultTemplateKey: number;
    orderId: number;
}

interface IState {
    doing: boolean;
    formErrors: any;
    formState: IEmailFormState;
    files: IFile[];
    attachments: IAttachment[];
    editorKey: number;
    initialized: boolean;
}

class EmailModal extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.state = {
            doing: false,
            formErrors: {},
            formState: {
                sender: 0,
                recipient: 0,
                cc: '',
                desiredSendTime: null,
                subject: '',
                body: '',
                template: -1
            },
            files: [],
            attachments: [],
            editorKey: 0,
            initialized: false
        };

        this.createEmail = this.createEmail.bind(this);
        this.onFormChange = this.onFormChange.bind(this);
        this.onBodyChange = this.onBodyChange.bind(this);
        this.onDateTimeChange = this.onDateTimeChange.bind(this);
        this.onImageUploaded = this.onImageUploaded.bind(this);
        this.onSelectorChange = this.onSelectorChange.bind(this);
        this.onAttachmentUpload = this.onAttachmentUpload.bind(this);
        this.onAttachmentDelete = this.onAttachmentDelete.bind(this);
    }

    componentDidMount(): void {
        this.forceUpdate();
    }

    componentDidUpdate(): void {
        if (!this.state.initialized) {
            let formState: IEmailFormState = assign({}, this.state.formState);
            formState.sender = this.props.senders.filter(
                (x: IEmailSender) => x.defaultForOrder
            )[0].id;

            if (this.props.recipients.length === 1) {
                formState.recipient = 1;
            }

            let templates = this.props.templates.filter(
                (t: ITemplate) => t.key === this.props.defaultTemplateKey
            );
            if (templates.length === 1) {
                formState.template = templates[0].id;
                formState.subject = templates[0].subject;
                formState.body = templates[0].content;
            }

            this.setState({
                doing: this.state.doing,
                formErrors: this.state.formErrors,
                formState: formState,
                files: this.state.files,
                editorKey: this.state.editorKey,
                initialized: true
            } as IState);
        }
    }

    onFormChange(e: any): void {
        let formState = assign({}, this.state.formState);
        let editorKey = this.state.editorKey;
        const value = e.target.value;

        formState[e.target.name] = value;

        this.setState({
            doing: this.state.doing,
            formErrors: this.state.formErrors,
            formState: formState,
            files: this.state.files,
            editorKey: editorKey
        } as IState);
    }

    onSelectorChange(e: any) {
        let formState = assign({}, this.state.formState);
        let editorKey = this.state.editorKey;
        const value = e.target.value;

        formState[e.target.name] = Number(value);
        if (e.target.name === Strings.templateSelectorName) {
            if (value === '') {
                formState.subject = '';
                formState.body = '';
            } else {
                let template = this.props.templates.filter((t: ITemplate) => {
                    return t.id.toString() === value;
                })[0];
                formState.subject = template.subject;
                formState.body = template.content;
            }

            editorKey = editorKey + 1;
        }

        this.setState({
            doing: this.state.doing,
            formErrors: this.state.formErrors,
            formState: formState,
            files: this.state.files,
            editorKey: editorKey
        } as IState);
    }

    onBodyChange(body: string): void {
        let formState = assign({}, this.state.formState);
        formState['body'] = body;

        this.setState({
            doing: this.state.doing,
            formErrors: this.state.formErrors,
            formState: assign({}, formState),
            files: this.state.files,
            editorKey: this.state.editorKey
        } as IState);
    }

    onDateTimeChange(moment: any): void {
        let formState = this.state.formState;

        formState.desiredSendTime = moment ? moment.toDate() : null;

        this.setState({
            doing: this.state.doing,
            formErrors: this.state.formErrors,
            formState: assign({}, formState),
            files: this.state.files,
            editorKey: this.state.editorKey
        } as IState);
    }

    onImageUploaded(reference: string, filename: string) {
        let files = assign([], this.state.files);
        files.push({ reference: reference, filename: filename });
        this.setState({
            doing: this.state.doing,
            formErrors: this.state.formErrors,
            formState: this.state.formState,
            files: files,
            editorKey: this.state.editorKey
        } as IState);
    }

    createEmail(): void {
        const attachments = this.state.attachments.map((attachment) => attachment.id);
        this.props.actions
            .createEmail(
                this.props.orderId,
                this.state.formState,
                this.props.recipients,
                attachments
            )
            .then(() => {
                notify.success(
                    Translate('view-order/basic-order/modal/create-email/success-notification')
                );
                this.props.close();
            })
            .catch((error: any) =>
                handleError(error, (errors: any): void => {
                    this.setState({
                        doing: this.state.doing,
                        formErrors: errors,
                        formState: this.state.formState,
                        files: this.state.files,
                        editorKey: this.state.editorKey
                    } as IState);
                })
            );
    }

    onAttachmentUpload(attachment: IAttachment) {
        const attachments = [...this.state.attachments, attachment];
        this.setState({ attachments });
    }

    onAttachmentDelete(id: number) {
        const attachments = [...this.state.attachments].filter(
            (attachment) => attachment.id !== id
        );

        this.setState({ attachments });
    }

    render(): any {
        return (
            <Modal
                title="view-order/basic-order/modal/create-email/title"
                readOnly={false}
                large={true}
                do={this.createEmail}
                doText="view-order/basic-order/modal/create-email/button-labels/do"
                close={this.props.close}
                doing={this.state.doing}
                doingText="view-order/basic-order/modal/create-email/button-labels/doing"
                cancelText="view-order/basic-order/modal/create-email/button-labels/cancel"
            >
                {this.state.initialized && (
                    <EmailForm
                        errors={this.state.formErrors}
                        state={this.state.formState}
                        senders={this.props.senders}
                        recipients={this.props.recipients}
                        templates={this.props.templates}
                        files={this.state.files}
                        attachments={this.state.attachments}
                        onAttachmentUpload={this.onAttachmentUpload}
                        onAttachmentDelete={this.onAttachmentDelete}
                        onChange={this.onFormChange}
                        onBodyChange={this.onBodyChange}
                        onDateTimeChange={this.onDateTimeChange}
                        onSelectorChange={this.onSelectorChange}
                        onImageUploaded={this.onImageUploaded}
                        editorKey={this.state.editorKey}
                    />
                )}
            </Modal>
        );
    }
}

function mapStateToProps(state: any, ownProps: any) {
    return {
        recipients: state.basicOrderInfo.newEmail.recipients,
        senders: state.basicOrderInfo.newEmail.senders,
        templates: state.basicOrderInfo.newEmail.templates,
        defaultTemplateKey: state.basicOrderInfo.newEmail.defaultTemplateKey
    };
}

function mapDispatchToProps(dispatch: any) {
    return {
        actions: {
            createEmail: (
                orderId: number,
                formState: IEmailFormState,
                recipients: IRecipient[],
                attachments: number[]
            ) => dispatch(Actions.createEmail(orderId, formState, recipients, attachments))
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(EmailModal);
